import React from 'react';

// Device Detection and Optimization Utility
// Detects mobile vs desktop and provides optimization settings

export const detectDevice = () => {
  const userAgent = navigator.userAgent;
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  
  // Mobile detection
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) || 
                   screenWidth <= 768;
  
  // Tablet detection
  const isTablet = (screenWidth > 768 && screenWidth <= 1024) || 
                   /iPad/i.test(userAgent);
  
  // Desktop detection
  const isDesktop = screenWidth > 1024 && !isMobile && !isTablet;
  
  // Touch capability
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Device orientation
  const isPortrait = screenHeight > screenWidth;
  const isLandscape = screenWidth > screenHeight;
  
  // Performance optimization based on device
  const optimization = {
    // Mobile optimizations
    mobile: {
      gridColumns: 1,
      cardSpacing: 'p-3',
      fontSize: 'text-sm',
      iconSize: 'w-4 h-4',
      buttonSize: 'px-3 py-2',
      animationSpeed: 'transition-all duration-200',
      // WCAG 2.5.5 target ~44×44 CSS px; Material often uses 48dp — use 44px as default token; add padding on dense rows if Android HIG is required.
      touchTarget: 'min-h-[44px]',
      scrollBehavior: 'smooth',
      modalSize: 'max-w-sm',
      sidebarCollapsed: true,
      showQuickActions: true,
      compactLayout: true,
      reducedAnimations: true,
      offlineMode: true,
      imageQuality: 'low',
      dataSyncInterval: 60000, // 1 minute
    },
    
    // Tablet optimizations
    tablet: {
      gridColumns: 2,
      cardSpacing: 'p-4',
      fontSize: 'text-base',
      iconSize: 'w-5 h-5',
      buttonSize: 'px-4 py-2',
      animationSpeed: 'transition-all duration-300',
      touchTarget: 'min-h-[44px]',
      scrollBehavior: 'smooth',
      modalSize: 'max-w-md',
      sidebarCollapsed: false,
      showQuickActions: true,
      compactLayout: false,
      reducedAnimations: false,
      offlineMode: true,
      imageQuality: 'medium',
      dataSyncInterval: 30000, // 30 seconds
    },
    
    // Desktop optimizations
    desktop: {
      gridColumns: 4,
      cardSpacing: 'p-3',
      fontSize: 'text-sm',
      iconSize: 'w-4 h-4',
      buttonSize: 'px-3 py-2',
      animationSpeed: 'transition-all duration-300',
      touchTarget: 'min-h-[36px]',
      scrollBehavior: 'auto',
      modalSize: 'max-w-md',
      sidebarCollapsed: false,
      showQuickActions: false,
      compactLayout: true,
      reducedAnimations: false,
      offlineMode: false,
      imageQuality: 'high',
      dataSyncInterval: 15000, // 15 seconds
    }
  };
  
  // Get current optimization settings
  const getOptimization = () => {
    if (isMobile) return optimization.mobile;
    if (isTablet) return optimization.tablet;
    return optimization.desktop;
  };
  
  // Responsive breakpoints for aged care use
  const breakpoints = {
    mobile: 768,
    tablet: 1024,
    desktop: 1200,
    largeDesktop: 1440
  };
  
  // Check if current screen size matches breakpoint
  const isBreakpoint = (breakpoint) => {
    return screenWidth <= breakpoints[breakpoint];
  };
  
  // Get optimal layout for aged care workflow
  const getAgedCareLayout = () => {
    if (isMobile) {
      return {
        navigation: 'top-nav', // Changed from 'bottom-tabs' to 'top-nav'
        dashboard: 'single-column',
        residents: 'list-view',
        medication: 'card-view',
        clinical: 'form-view',
        alerts: 'notification-center',
        family: 'chat-interface'
      };
    } else if (isTablet) {
      return {
        navigation: 'side-tabs',
        dashboard: 'two-column',
        residents: 'grid-view',
        medication: 'table-view',
        clinical: 'split-view',
        alerts: 'sidebar',
        family: 'dashboard-integrated'
      };
    } else {
      return {
        navigation: 'top-nav',
        dashboard: 'multi-column',
        residents: 'detailed-view',
        medication: 'advanced-table',
        clinical: 'full-screen',
        alerts: 'floating-panel',
        family: 'dedicated-portal'
      };
    }
  };
  
  // Performance monitoring for aged care
  const performanceSettings = {
    mobile: {
      maxConcurrentRequests: 2,
      cacheSize: '50MB',
      imageCompression: 0.7,
      lazyLoading: true,
      prefetching: false,
      backgroundSync: true
    },
    tablet: {
      maxConcurrentRequests: 4,
      cacheSize: '100MB',
      imageCompression: 0.8,
      lazyLoading: true,
      prefetching: true,
      backgroundSync: true
    },
    desktop: {
      maxConcurrentRequests: 8,
      cacheSize: '200MB',
      imageCompression: 0.9,
      lazyLoading: false,
      prefetching: true,
      backgroundSync: false
    }
  };
  
  return {
    isMobile,
    isTablet,
    isDesktop,
    hasTouch,
    isPortrait,
    isLandscape,
    screenWidth,
    screenHeight,
    userAgent,
    optimization: getOptimization(),
    layout: getAgedCareLayout(),
    performance: isMobile ? performanceSettings.mobile : 
                 isTablet ? performanceSettings.tablet : 
                 performanceSettings.desktop,
    breakpoints,
    isBreakpoint
  };
};

// Hook for device detection
export const useDeviceDetection = () => {
  const [deviceInfo, setDeviceInfo] = React.useState(detectDevice());
  
  React.useEffect(() => {
    const handleResize = () => {
      setDeviceInfo(detectDevice());
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  
  return deviceInfo;
};

// Aged care specific optimizations
export const getAgedCareOptimizations = (deviceInfo) => {
  const { isMobile, isTablet, isDesktop } = deviceInfo;
  
  return {
    // Doctor optimizations
    doctor: {
      patientList: isMobile ? 'compact' : 'detailed',
      noteWriting: isMobile ? 'voice-input' : 'keyboard',
      prescriptionEntry: isMobile ? 'quick-form' : 'full-form',
      roundsView: isMobile ? 'list' : 'grid',
      alertsDisplay: isMobile ? 'push' : 'sidebar'
    },
    
    // Nurse optimizations
    nurse: {
      medicationRounds: isMobile ? 'barcode-scan' : 'manual-entry',
      residentCards: isMobile ? 'swipe' : 'click',
      quickActions: isMobile ? 'floating-button' : 'toolbar',
      alertsResponse: isMobile ? 'one-tap' : 'detailed-view',
      shiftHandover: isMobile ? 'voice-notes' : 'text-notes'
    },
    
    // Family optimizations
    family: {
      updatesView: isMobile ? 'timeline' : 'dashboard',
      messaging: isMobile ? 'chat-interface' : 'email-integration',
      photoSharing: isMobile ? 'camera-upload' : 'file-upload',
      notifications: isMobile ? 'push' : 'email',
      billingView: isMobile ? 'summary' : 'detailed'
    },
    
    // Manager optimizations
    manager: {
      facilityOverview: isMobile ? 'summary-cards' : 'detailed-metrics',
      staffManagement: isMobile ? 'list-view' : 'table-view',
      reporting: isMobile ? 'charts' : 'interactive-dashboard',
      qualityMetrics: isMobile ? 'key-indicators' : 'comprehensive-view'
    }
  };
};
